import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo, NexusSearchSchemaEntryNames, NexusSearchSchemaTypes, ProductTechnicalPropertyTypeIds} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Utils, {createUUId} from "../../../../../../../core/services/utils/utils";
import axios from "axios";
import useInfiniteScrollSearchUtils from "../../../../../../hooks/use-infinite-scroll-search-utils";
import {stringifyUrl} from "query-string";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {Box, Button, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import InfiniteScroll from "react-infinite-scroller";
import Grid from "@mui/material/Unstable_Grid2";
import LookupProductCard from "../../../../../../components/app-specific/lookup-product-card";
import AggregatedLookupProductCard from "../../../../../../components/app-specific/aggregated-lookup-product-card";
import Api from "../../../../../../../core/services/api/api";
import ProductsLostSearchContainer from "./products-lost-search-container";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import ProductsOtherIndustriesContainer from "./products-other-industries-container";
import {ArrowDownwardOutlined, SortRounded, ViewCompactRounded, ViewModuleOutlined} from "@mui/icons-material";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";
import ProductsDataGrid from "./products-data-grid";
import useAggregatedVariantsNexusSearch from "../../../../../../hooks/use-aggregated-variants-nexus-search";
import {useTranslation} from "react-i18next";
import PartnerIndustryRules from "../../../../../../../core/services/partner-industry-rules";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";
import CacheService from "../../../../../../../core/services/cache/cache_service";

const chunkSize = 25;
const LOOKUP_INITIAL_PAGE_SIZE = 30;
const LOOKUP_LIVE_AVAILABILITY_FETCH_BATCH = 20;
const TOGGLE_BUTTON_SIZE = 30;
const TOGGLE_TOOLBAR_STACK_SX = {
    border: '1px solid transparent',
    borderColor: 'divider',
    p: 0.5,
    borderRadius: 1,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Match outer box of the two-button view mode control (2×30px buttons + 0.25rem gap + stack padding).
    minWidth: TOGGLE_BUTTON_SIZE * 2 + 4 + 8,
    minHeight: TOGGLE_BUTTON_SIZE + 8,
};
const TOGGLE_TOOLBAR_GROUP_SX = {
    width: '100%',
    '& .MuiToggleButtonGroup-grouped': {
        borderRadius: '0.5rem',
        width: TOGGLE_BUTTON_SIZE,
        height: TOGGLE_BUTTON_SIZE,
        border: 'none',
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
        marginLeft: '.25rem',
    },
};

const normalizePartNo = (value) => value?.trim()?.toUpperCase() ?? '';

const toPartNos = (list) => list.flatMap((e) => {
    if (e.isAggregated && e.members?.length) {
        return e.members.map((m) => m.partNo);
    }
    return [e.partNo];
});

/**
 * Selects unfetched part numbers for live availability based on the partner mode.
 * When the candidate set is the full loaded list, fetch 20 at a time.
 * @param {'searchedAndMainResults' | 'searched' | 'always'} mode
 * @param {boolean} hasSearch
 * @param {string[]} allPartNos
 * @param {string[]} mainPartNos
 * @param {Record<string, unknown>} availabilities
 * @returns {string[]}
 */
const partNosForLiveAvailabilityFetch = (mode, hasSearch, allPartNos, mainPartNos, availabilities) => {
    let candidates;
    switch (mode) {
        case 'always':
            candidates = allPartNos;
            break;
        case 'searched':
            candidates = hasSearch ? allPartNos : [];
            break;
        case 'searchedAndMainResults':
        default:
            candidates = hasSearch ? mainPartNos : [];
            break;
    }
    const unfetched = candidates.filter((e) => !availabilities[e]);
    const fetchingFullList = candidates.length === allPartNos.length && allPartNos.length > 0;
    return fetchingFullList ? unfetched.slice(0, LOOKUP_LIVE_AVAILABILITY_FETCH_BATCH) : unfetched;
};

/**
 * @param {object} item
 * @param {string} normalizedKeyword
 * @returns {boolean}
 */
const productMatchesExactKeyword = (item, normalizedKeyword) => {
    if (!normalizedKeyword) {
        return false;
    }
    if (item.isAggregated && item.members?.length) {
        return item.members.some((member) => normalizePartNo(member.partNo) === normalizedKeyword)
            || normalizePartNo(item.selectedPartNo) === normalizedKeyword
            || normalizePartNo(item.partNo) === normalizedKeyword;
    }
    return normalizePartNo(item.partNo) === normalizedKeyword;
};

/**
 * Moves exact keyword partNo matches to the front of products-list results.
 * @param {Array} items
 * @param {string|null|undefined} keyword
 * @param {boolean} inProductsList
 * @returns {Array}
 */
const prioritizeExactPartNoKeywordMatch = (items, keyword, inProductsList) => {
    if (!inProductsList || !items?.length) {
        return items ?? [];
    }
    const normalizedKeyword = normalizePartNo(keyword);
    if (!normalizedKeyword) {
        return items;
    }
    const exactMatches = [];
    const others = [];
    for (const item of items) {
        (productMatchesExactKeyword(item, normalizedKeyword) ? exactMatches : others).push(item);
    }
    if (!exactMatches.length) {
        return items;
    }
    return [...exactMatches, ...others];
};


const LookupSearchViewProducts = ({
                                      searchData,
                                      setSearchData,
                                      searchSchema,
                                      selectedWarehouses,
                                      selectedPartner,
                                      minRelevanceScore,
                                      setSearchToken,
                                      searchToken,
                                      setFacets,
                                      resetQueryKeyword,
                                      inProductsList,
                                      getScrollParent,
                                      advancedSearch,
                                      industries,
                                      readonly,
                                  }) => {
    const [otherIndustriesHaveResults, setOtherIndustriesHaveResults] = useState(false)
    const [paginationInfo, setPaginationInfo] = useState({
        ...DefaultPaginationInfo,
        pageSize: LOOKUP_INITIAL_PAGE_SIZE,
    });
    const [products, setProducts] = useState([]);
    const [availabilities, setAvailabilities] = useState({});
    const [fetchingProductAvailabilities, setFetchingProductAvailabilities] = useState(false);
    const [prices, setPrices] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(true);
    const productsRef = useRef([]);
    productsRef.current = products;
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const {t: tProducts} = useTranslation('views', {keyPrefix: 'app-specific.lookup-v2-search-products'});
    const searchTokenRef = useRef(searchToken);
    const containerRef = useRef();
    const advancedSearchRef = useRef(advancedSearch);
    advancedSearchRef.current = advancedSearch;
    const prevSelectedWarehouseIds = useRef(null);
    const partnerInformation = usePartnerInformation();
    const partnerId = useMemo(
        () => partnerInformation?.id ?? CacheService.getOperatorPartnerId(),
        [partnerInformation?.id],
    );
    const skipMainOtherBucketing = useMemo(
        () => PartnerIndustryRules.disablesNexusMainOtherBucketing(partnerId),
        [partnerId],
    );

    const [aggregationToggleOn, setAggregationToggleOn] = useState(false);
    const [loadingAggregationToggle, setLoadingAggregationToggle] = useState(true);
    const {highestVersion: getCachedAggregationToggle, save: saveCachedAggregationToggle} = useSavedComponentsState({
        name: "products-list-aggregation-enabled",
        version: '1.0.0',
    });

    const {
        shouldAggregate,
        aggregationSupported,
        aggregationGatePending,
        aggregationActive,
        setAggregationActive,
        varianceProperties,
        resetSession,
        loadAggregatedPage,
        applySelection,
        uiPageSize: AGGREGATION_UI_PAGE_SIZE,
        apiPageSize: AGGREGATION_API_PAGE_SIZE,
    } = useAggregatedVariantsNexusSearch({
        industryId: searchData.industryId,
        filters: searchData.filters,
        searchSchema,
        userAggregationEnabled: !inProductsList || aggregationToggleOn,
    });

    const selectedWarehouseIds = useMemo(() => {
        const warehouseIds = selectedWarehouses?.map(e => e.id) ?? [];
        warehouseIds.sort();
        if (prevSelectedWarehouseIds.current?.join(',') === warehouseIds.join(',')) {
            return prevSelectedWarehouseIds.current;
        }
        prevSelectedWarehouseIds.current = warehouseIds;
        return warehouseIds;
    }, [selectedWarehouses]);

    const firstWarehouseCode = useMemo(() => selectedWarehouses?.[0]?.code ?? undefined, [selectedWarehouses]);

    /**
     * Resets the availabilities when the selected warehouses change.
     */
    useEffect(() => {
        setAvailabilities({});
    }, [selectedWarehouseIds]);

    /**
     * Resets the availabilities when the selected warehouses change.
     */
    useEffect(() => {
        setPrices({});
    }, [selectedPartner?.id]);

    const maxScore = useMemo(() => {
        if (skipMainOtherBucketing || !products.length) {
            return 0;
        }
        return Math.max(...products.map(p => p.relevanceScore ?? p.searchScore));
    }, [products, skipMainOtherBucketing]);
    const thresholdScore = useMemo(() => {
        if (skipMainOtherBucketing) {
            return 0;
        }
        return maxScore * (minRelevanceScore / 100);
    }, [maxScore, minRelevanceScore, skipMainOtherBucketing]);

    const allProductNos = useMemo(() => toPartNos(products), [products]);

    const mainProductNos = useMemo(() => {
        // Horsebarn (and similar): all loaded results as main — no score threshold split.
        if (skipMainOtherBucketing) {
            return allProductNos;
        }

        // Filter products based on their value percentile and minRelevanceScore
        return toPartNos(
            products.filter(e => (e.relevanceScore ?? e.searchScore) >= thresholdScore),
        );
    }, [allProductNos, products, thresholdScore, skipMainOtherBucketing]);

    const hasAnySearchCriteriaApplied = useMemo(() => (
        searchData.filters.length > 0 ||
        !!searchData.keyword ||
        (inProductsList && (
            typeof searchData.isVisible === 'boolean' ||
            typeof searchData.isPublicForBuyers === 'boolean'
        ))
    ), [searchData.filters.length, searchData.keyword, inProductsList, searchData.isVisible, searchData.isPublicForBuyers])

    const allProductsAreMain = useMemo(() => {
        // Check if all products are considered main products
        return mainProductNos.length === products.length;
    }, [products.length, mainProductNos.length]);

    const mainProductNosWithNonFetchedPrices = useMemo(() => {
        // Filter or slice based on whether all products are main products
        return !hasAnySearchCriteriaApplied
            ? []
            : allProductsAreMain
                ? mainProductNos.filter(e => !prices[e]).slice(0, 20)
                : mainProductNos.filter(e => !prices[e]);
    }, [hasAnySearchCriteriaApplied, allProductsAreMain, mainProductNos, prices]);

    const lookupLiveAvailabilityMode = useMemo(
        () => PartnerIndustryRules.lookupLiveAvailabilityMode(partnerId),
        [partnerId],
    );

    const mainProductNosWithNonFetchedAvailabilities = useMemo(() => {
        return partNosForLiveAvailabilityFetch(
            lookupLiveAvailabilityMode,
            hasAnySearchCriteriaApplied,
            allProductNos,
            mainProductNos,
            availabilities,
        );
    }, [allProductNos, availabilities, hasAnySearchCriteriaApplied, lookupLiveAvailabilityMode, mainProductNos]);

    const searchRequest = useMemo(() => {
        return ({
            industryId: searchData.industryId,
            filters: searchData.filters,
            keyword: searchData.keyword?.trim()?.length ? searchData.keyword?.trim() : null,
            pagination: {pageSize: paginationInfo.pageSize, currentPage: paginationInfo.currentPage},
            orderBy: searchData.orderBy,
            ...(inProductsList && {
                isVisible: searchData.isVisible,
                isPublicForBuyers: searchData.isPublicForBuyers,
            }),
        })
    }, [paginationInfo.currentPage, paginationInfo.pageSize, searchData.filters, searchData.industryId, searchData.keyword, searchData.orderBy, inProductsList, searchData.isVisible, searchData.isPublicForBuyers])

    /**
     * Updates the search token ref with the latest search token.
     */
    useLayoutEffect(() => {
        searchTokenRef.current = searchToken;
    }, [searchToken])

    /**
     * With each change in the filters, resets the pagination and loading state.
     */
    useLayoutEffect(() => {
        if (aggregationGatePending) {
            return;
        }
        const nextPageSize = shouldAggregate ? AGGREGATION_UI_PAGE_SIZE : LOOKUP_INITIAL_PAGE_SIZE;
        setPaginationInfo((prev) => {
            if (
                prev.currentPage === DefaultPaginationInfo.currentPage
                && prev.pageSize === nextPageSize
                && prev.length === DefaultPaginationInfo.length
            ) {
                return prev;
            }
            return {...DefaultPaginationInfo, pageSize: nextPageSize};
        });
        setLoadingProducts(true);
        resetSession();
    }, [searchData.filters, searchData.industryId, searchData.keyword, searchData.orderBy, searchData.isVisible, searchData.isPublicForBuyers, inProductsList, shouldAggregate, aggregationGatePending, AGGREGATION_UI_PAGE_SIZE, resetSession]);

    /**
     * Maps a raw Nexus API item into a search hit used by aggregation / cards.
     */
    const mapNexusItemToHit = useCallback((e, configuration) => ({
        searchScore: e.score,
        images: e.images.map(img => configuration?.PartImageURL?.concat(img)),
        partNo: e.partNumber,
        title: e.title,
        id: e.id,
        rootCategoryName: e.parentCategory,
        leafCategoryName: e.category,
        codes: e.foundedCodes,
        tags: e.tags
            ?.filter(tag => !!(tag?.name ?? '').trim())
            ?.map(tag => ({
                id: createUUId(),
                name: tag.name,
                iconFileName: tag.iconFileName
                    ? configuration.TagIconUrlBaseAddress.concat?.(tag.iconFileName)
                    : undefined,
            })) ?? [],
        additionalProperties: e.properties ?? [],
        variety: e.variety,
        deliveryMode: e.deliveryMode,
    }), []);

    /**
     * Performs a nexus search based on the given parameters.
     * @type {(function(*, *=): Promise<undefined|[(*[]),number, (*[]) | string | undefined]>)}
     */
    const performNexusSearch = useCallback(async (forApi, cancelToken) => {
        let response;
        if (inProductsList) {
            response = await Api.nexusSearchProductsList(forApi, cancelToken)
        } else {
            response = await Api.nexusSearch(forApi, cancelToken)
        }
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const productsList = response.data.nexusResponseParts?.items.map((e) =>
                mapNexusItemToHit(e, response.configuration),
            )
            const productCounts = response.data.nexusResponseParts?.totalCount ?? 0;
            const searchToken = response.data.searchToken ?? undefined;
            const facets = response.data.nexusResponseFacets
                .map(e => ({
                    name: e.name,
                    values: e.items.map(e => ({
                        value: e.value,
                        affectedPartsCount: e.count,
                    }))
                }))
                .filter(e => {
                    const schemaEntry = searchSchema?.entries?.find(s => s.name === e.name);
                    switch (schemaEntry?.type) {
                        case NexusSearchSchemaTypes.propertyGroup:
                        case NexusSearchSchemaTypes.field:
                            return true; // keep all properties and fields even if they are empty
                        case NexusSearchSchemaTypes.property:
                        default:
                            return e.values.length;
                    }
                })
            return [productsList, productCounts, facets, searchToken];
        }
    }, [inProductsList, isMounted, mapNexusItemToHit, searchSchema?.entries])

    /**
     * Builds the API request body for a given pagination page.
     */
    const buildSearchApiBody = useCallback((pagination) => {
        const base = {
            isAdvancedMode: advancedSearchRef.current,
            industryId: searchRequest.industryId,
            searchToken: searchTokenRef.current,
            keyword: searchRequest.keyword,
            pagination,
            sortBy: searchRequest.orderBy
                ? {industryIndexStructureId: searchRequest.orderBy.id, isDesc: searchRequest.orderBy.isDescending}
                : null,
            filters: searchData.filters.map(e => ({
                industryIndexStructureId: e.filterId,
                value: e.value?.toString() ?? null,
            })),
            ...(shouldAggregate ? {useVarianceAggregator: true} : {}),
        };
        if (inProductsList) {
            return {
                ...base,
                isVisible: searchRequest.isVisible,
                isPublicForBuyers: searchRequest.isPublicForBuyers,
            };
        }
        return base;
    }, [
        inProductsList,
        searchRequest.industryId,
        searchRequest.keyword,
        searchRequest.isVisible,
        searchRequest.isPublicForBuyers,
        searchRequest.orderBy,
        searchData.filters,
        shouldAggregate,
    ]);

    /**
     * Fetches the products based on the given search parameters.
     * @type {(function(import("axios").CancelTokenSource): Promise<void>)}
     */
    const fetchProducts = useCallback(async (cancelTokenSource) => {
        setLoadingProducts(true);
        if (searchRequest.pagination.currentPage === 1) {
            containerRef.current?.parentElement?.parentElement?.scrollTo(0, 0);
            setProducts([]);
            resetSession();
        }

        if (shouldAggregate) {
            const isReset = searchRequest.pagination.currentPage === 1;
            const result = await loadAggregatedPage({
                reset: isReset,
                previousCards: isReset ? [] : productsRef.current,
                config: {
                    apiPageSize: AGGREGATION_API_PAGE_SIZE,
                    uiPageSize: AGGREGATION_UI_PAGE_SIZE,
                },
                fetchPage: async (apiPage, pageSize) => {
                    const forApi = buildSearchApiBody({pageSize, currentPage: apiPage});
                    const res = await performNexusSearch(forApi, cancelTokenSource.token);
                    if (!res) {
                        return {hits: [], totalCount: 0};
                    }
                    const [hits, totalCount, facets, token] = res;
                    return {
                        hits,
                        totalCount,
                        meta: {facets, searchToken: token},
                    };
                },
            });

            if (!isMounted()) {
                return;
            }
            if (!cancelTokenSource.token?.reason) {
                setLoadingProducts(false);
            }

            const meta = result.firstPageMeta;
            if (isReset && meta?.facets) {
                resetQueryKeyword?.();
                setFacets(meta.facets);
            }
            if (meta?.searchToken && searchTokenRef.current !== meta.searchToken) {
                setSearchToken?.(meta.searchToken);
            }

            setProducts(prioritizeExactPartNoKeywordMatch(result.cards, searchRequest.keyword, inProductsList));

            const hasMore =
                result.session.hasMoreServerPages
                || (result.session.dictionary.size > result.session.emittedVarietyKeys.length);
            setPaginationInfo((prev) => ({
                ...prev,
                // Hide meaningful totals while aggregating; keep length high enough for infinite scroll.
                length: hasMore
                    ? Math.max(result.cards.length + AGGREGATION_UI_PAGE_SIZE, prev.pageSize * prev.currentPage + 1)
                    : result.cards.length,
            }));
            return;
        }

        setAggregationActive(false);
        const forApi = buildSearchApiBody(searchRequest.pagination);
        const res = await performNexusSearch(forApi, cancelTokenSource.token);
        if (!isMounted())
            return;
        if (!cancelTokenSource.token?.reason) {
            setLoadingProducts(false);
        }
        if (!!res) {
            const [productsList, productCounts, facets, searchToken] = res;
            if (forApi.pagination.currentPage === 1) {
                resetQueryKeyword?.();
                setPaginationInfo(prevState => ({...prevState, length: productCounts}))
                setFacets(facets);
                setProducts(prioritizeExactPartNoKeywordMatch([...productsList], searchRequest.keyword, inProductsList));
            } else {
                setProducts((prevState) => prioritizeExactPartNoKeywordMatch(
                    [...prevState, ...productsList],
                    searchRequest.keyword,
                    inProductsList,
                ));
            }
            if (forApi.searchToken !== searchToken) {
                setSearchToken?.(searchToken)
            }
        }
    }, [
        shouldAggregate,
        varianceProperties,
        buildSearchApiBody,
        searchRequest.pagination,
        performNexusSearch,
        isMounted,
        resetQueryKeyword,
        setFacets,
        setSearchToken,
        loadAggregatedPage,
        resetSession,
        setAggregationActive,
        AGGREGATION_API_PAGE_SIZE,
        AGGREGATION_UI_PAGE_SIZE,
        inProductsList,
        searchRequest.keyword,
    ])

    /**
     * Fetches the products based on the given search parameters.
     */
    useEffect(() => {
        if (!searchRequest?.industryId)
            return;
        if (aggregationGatePending) {
            setLoadingProducts(true);
            return;
        }
        const cancelTokenSource = axios.CancelToken.source();
        void fetchProducts(cancelTokenSource)
        return () => cancelTokenSource.cancel();
    }, [fetchProducts, searchRequest?.industryId, aggregationGatePending]);

    /**
     * Fetches the prices of the products
     * @type {(function(*, *=): Promise<void>)}
     */
    const getProductPrices = useCallback(async (productNos) => {
        // TODO: call api to get prices for a list of products.
        // TODO: add the access level for sales --> product prices
        const batches = productNos.reduce((arr, partNo, index) => {
            if (index % chunkSize === 0) {
                arr.push([]);
            }
            arr[arr.length - 1].push(partNo);
            return arr;
        }, []);
        for (const batch of batches) {
            await new Promise(r => setTimeout(r, 1000));
            const response = {
                resultFlag: false,
                data: Array.from({length: batch.length}).map((_, e) => ({
                    partNumber: batch[e],
                    originalPrice: 1000 * (e + 1),
                    newPrice: e < 5 ? null : 800 * (e + 1),
                }))
            }
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setPrices(prevState => ({
                    ...prevState,
                    ...response.data.reduce((acc, e) => ({...acc, [e.partNumber]: e}), {})
                }))
            }
        }
    }, [isMounted])

    /**
     * Fetches the prices of the main products in the search result.
     */
    useEffect(() => {
        if (!mainProductNosWithNonFetchedPrices.length || !selectedPartner?.id || !operatorAccessLevels.packages.sales.pricesInLookup)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getProductPrices(mainProductNosWithNonFetchedPrices, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [mainProductNosWithNonFetchedPrices, getProductPrices, selectedPartner?.id, operatorAccessLevels.packages.sales.pricesInLookup]);

    /**
     * With each change in the selected customer, reset the fetched / cached prices of products.
     */
    useEffect(() => {
        setPrices({});
    }, [selectedPartner?.id]);

    /**
     * Fetches the availabilities of the products
     * @type {(function(*, *=): Promise<void>)}
     */
    const getProductAvailabilities = useCallback(async (productNos) => {
        setFetchingProductAvailabilities(true);
        const batches = productNos.reduce((arr, productNos, index) => {
            if ((index % chunkSize) === 0) {
                arr.push([]);
            }
            arr[arr.length - 1].push(productNos);
            return arr;
        }, []);
        for (const batch of batches) {
            const response = await Api.getProductAvailabilitiesV2({
                partNumbers: batch,
                warehouseIds: selectedWarehouseIds,
            })
            if (!isMounted())
                return
            if (response?.resultFlag) {
                setAvailabilities(prevState => ({
                    ...prevState,
                    ...productNos.reduce((acc, partNo) => {
                        const responseData = response.data.find(r => r.partNumber === partNo);
                        const result = selectedWarehouseIds.map(warehouseId => {
                            const localWarehouse = responseData?.localWarehouses?.find(e => e.warehouseId === warehouseId);
                            const partner = responseData?.partners?.find(e => e.partnerName === selectedPartner?.name);
                            const partnerWarehouse = partner?.warehouses?.find(e => e.warehouseName === localWarehouse?.warehouseName);
                            return ({
                                _has: !!responseData,
                                partner: partner?.partnerName,
                                warehouse: localWarehouse?.warehouseCode ?? partnerWarehouse?.warehouseName,
                                fullName: `${localWarehouse?.warehouseName ?? partnerWarehouse?.warehouseName} ${localWarehouse?.warehouseCode ? `(${localWarehouse?.warehouseCode})` : ''}`,
                                availability: localWarehouse?.availableQty ?? partnerWarehouse?.title,
                                availabilityAltText: partnerWarehouse?.title,
                                availabilityColorCode: partnerWarehouse?.colorStatusCode,
                            })
                        });
                        return ({
                            ...acc,
                            [partNo]: result,
                        })
                    }, {}),
                }))
            }
        }
        if (!isMounted())
            return;
        setFetchingProductAvailabilities(false);
    }, [isMounted, selectedPartner?.name, selectedWarehouseIds])

    /**
     * Fetches the availabilities of the main products in the search result.
     */
    useEffect(() => {
        if (!mainProductNosWithNonFetchedAvailabilities.length || !selectedWarehouseIds?.length || fetchingProductAvailabilities || !operatorAccessLevels.packages.bizInventory.availabilityInLookup)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getProductAvailabilities(mainProductNosWithNonFetchedAvailabilities, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [mainProductNosWithNonFetchedAvailabilities, getProductAvailabilities, selectedWarehouseIds?.length, fetchingProductAvailabilities, operatorAccessLevels.packages.bizInventory.availabilityInLookup])

    /**
     * Submits a product selection in the server.
     * @type {(function(*, *): Promise<void>)}
     */
    const submitProductSelection = useCallback(async (e, product, searchToken) => {
        if (inProductsList || !searchToken)
            return
        void Api.submitNexusSearchClick({
            searchToken: searchToken,
            partId: product.id
        })
    }, [inProductsList])

    /**
     * Fetches the extra info of the product
     * @type {(function(*): Promise<void>)}
     */
    const fetchProductExtraInfo = useCallback(async (product) => {
        const promises = [];
        if (!!selectedPartner?.id && operatorAccessLevels.packages.sales.pricesInLookup) {
            promises.push(getProductPrices([product.partNo]));
        }
        if (!!selectedWarehouseIds?.length && operatorAccessLevels.packages.bizInventory.availabilityInLookup) {
            promises.push(getProductAvailabilities([product.partNo]));
        }
        await Promise.all(promises);
    }, [selectedPartner?.id, operatorAccessLevels.packages.sales.pricesInLookup, operatorAccessLevels.packages.bizInventory.availabilityInLookup, selectedWarehouseIds?.length, getProductPrices, getProductAvailabilities])

    const {loadMoreData, hasMoreToLoad, loadPageData} = useInfiniteScrollSearchUtils({
        dataLength: products.length,
        searchData: {
            loading: loadingProducts,
            setLoading: setLoadingProducts,
            paginationInfo: paginationInfo,
            setPaginationInfo: setPaginationInfo,
        },
    });

    const showMainResultsLoading = loadingProducts;

    /**
     * Fetches the products based on the given search parameters.
     * @type {function(*, *, *, *): string}
     */
    const getProductNavigationLink = useCallback(((partNo, codes, id, searchToken) =>
            !operatorAccessLevels.packages.lookup.productInformation && inProductsList
                ? undefined
                : !operatorAccessLevels.packages.lookup.lookupProductInformation && !inProductsList
                    ? undefined
                    : stringifyUrl({
                            url: inProductsList
                                ? routeFunctions.products.single(id)
                                : routeFunctions.lookup.single(partNo),
                            query: inProductsList
                                ? {}
                                : {
                                    searchToken: searchToken,
                                    codes: codes,
                                    warehouseCode: firstWarehouseCode
                                }
                        }
                        , {arrayFormat: 'bracket'})),
        [inProductsList, firstWarehouseCode, operatorAccessLevels.packages.lookup.productInformation, operatorAccessLevels.packages.lookup.lookupProductInformation]);

    const mapHitToDisplayProduct = useCallback((product) => {
        const priceInfo = prices[product.partNo];
        const availabilityInfo = availabilities[product.partNo];
        return {
            id: product.id,
            info: product,
            priceInfo: priceInfo,
            availabilityInfo: availabilityInfo,
            additionalProperties: product.additionalProperties?.map(e => {
                const schemaEntry = searchSchema?.entries?.find(s => s.name === e.name);
                return {
                    typeId: schemaEntry?.dataType,
                    nullReplacement: schemaEntry?.nullReplacement,
                    title: schemaEntry?.title ?? e.name,
                    displayValue: Utils.formatTechnicalPropertyValueDisplay(schemaEntry?.dataType, e.value, schemaEntry?.nullReplacement),
                    ...(schemaEntry?.dataType === ProductTechnicalPropertyTypeIds.color ? {tooltipValue: e.value} : {}),
                    name: e.name,
                    value: e.value,
                }
            }) ?? [],
            link: {
                href: getProductNavigationLink(product.partNo, product.codes, product.id, searchTokenRef.current),
                replace: false,
            },
        };
    }, [prices, availabilities, getProductNavigationLink, searchSchema?.entries]);

    const [mainProducts, relevantProducts] = useMemo(() => {
        const productsWithPricesAndAvailability = products.map(mapHitToDisplayProduct);
        const [mainProducts, relevantProducts] = productsWithPricesAndAvailability.reduce((acc, e) => {
            if (mainProductNos.includes(e.info.partNo)) {
                acc[0].push(e);
            } else {
                acc[1].push(e);
            }
            return acc;
        }, [[], []]);
        return [mainProducts, relevantProducts];
    }, [products, mapHitToDisplayProduct, mainProductNos]);


    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [loadingViewMode, setLoadingViewMode] = useState(true);
    const {highestVersion: getCachedViewMode, save: saveCachedViewMode} = useSavedComponentsState({name: "products-list-view-mode", version: '1.0.0'});

    /**
     * Fetches the cached view mode.
     * @type {(function(): Promise<void>)}
     */
    const getViewMode = useCallback(async () => {
        setLoadingViewMode(true);
        const cachedMode = await getCachedViewMode();
        if (cachedMode?.data) {
            setViewMode(cachedMode.data);
        }
        setLoadingViewMode(false);
    }, [getCachedViewMode]);

    /**
     * Loads the cached view mode on component mount.
     */
    useEffect(() => {
        void getViewMode();
    }, [getCachedViewMode, getViewMode]);

    /**
     * Fetches the cached aggregation toggle preference.
     */
    const getAggregationToggle = useCallback(async () => {
        setLoadingAggregationToggle(true);
        const cached = await getCachedAggregationToggle();
        if (cached?.data === true) {
            setAggregationToggleOn(true);
        }
        setLoadingAggregationToggle(false);
    }, [getCachedAggregationToggle]);

    /**
     * Loads the cached aggregation toggle on component mount.
     */
    useEffect(() => {
        if (!inProductsList) {
            setLoadingAggregationToggle(false);
            return;
        }
        void getAggregationToggle();
    }, [getAggregationToggle, inProductsList]);

    /**
     * Toggles aggregation on/off for the products list.
     */
    const toggleAggregation = useCallback(() => {
        setAggregationToggleOn(prev => {
            const next = !prev;
            void saveCachedAggregationToggle(next);
            return next;
        });
    }, [saveCachedAggregationToggle]);

    /**
     * Toggles the view mode between grid and list.
     * @type {(function(): void)|*}
     */
    const toggleViewMode = useCallback(() => {
        setViewMode(prevMode => {
            const newMode = prevMode === 'grid' ? 'list' : 'grid';
            void saveCachedViewMode(newMode);
            return newMode;
        });
    }, [saveCachedViewMode]);

    const [comparing, setComparing] = useState({});
    const [inCompareList, setInCompareList] = useState({});
    const [gettingExtraInfo, setGettingExtraInfo] = useState({});

    /**
     * Toggles the inCompareList
     * @type {(function(): Promise<*>)|*}
     */
    const toggleInCompareList = useCallback(async (e, info) => {
        e.preventDefault();
        e.stopPropagation();
        setComparing((prev) => ({...prev, [info.id]: true}));
        const response = inCompareList[info.id] ? await Api.removePartFromCompareList(info.id) : await Api.addPartToCompareList({partId: info.id})
        if (!isMounted())
            return;
        if (response?.resultFlag || response?.errorCode === -1) {
            setInCompareList((prev => ({...prev, [info.id]: !inCompareList[info.id]})));
        }
        setComparing((prev) => ({...prev, [info.id]: false}));
    }, [inCompareList, isMounted])


    /**
     * On Get Extra Info Clicked
     * @type {(function(*): void)}
     */
    const onGetExtraInfoClicked = useCallback(async (e, info) => {
        e.preventDefault();
        e.stopPropagation();
        setGettingExtraInfo(prev => ({...prev, [info.id]: true}));
        await fetchProductExtraInfo(info)
        if (!isMounted())
            return;
        setGettingExtraInfo(prev => ({...prev, [info.id]: false}));
    }, [fetchProductExtraInfo, isMounted])

    /**
     * On Product Clicked.
     * @type {(function(*): void)}
     */
    const onProductClicked = useCallback((e, info, link) => {
        if (readonly || !link.href) {
            e.preventDefault();
            return;
        }
        e.stopPropagation();
        void submitProductSelection?.(e, info, searchToken)
    }, [submitProductSelection, readonly, searchToken])

    const varietyFilterId = useMemo(() => searchSchema.entries.find(s => s.name === NexusSearchSchemaEntryNames.variety)?.id ?? null, [searchSchema.entries]);
    const selectedVariety = useMemo(() => searchData.filters.find(e => e.filterId === varietyFilterId)?.value ?? null, [searchData.filters, varietyFilterId]);

    /**
     * Sets the variance of the search data.
     * If the variance is already set for the same product, unset it.
     */
    const onVarianceClicked = useCallback((info) => {
        setSearchData(prevState => {
            const varianceFilter = prevState.filters.find(e => e.filterId === varietyFilterId);
            return ({
                ...prevState,
                // we add the filters for searching purposes and add variance property for UI only.
                filters: !!varianceFilter && varianceFilter.value === info.variety
                    ? prevState.filters.filter(e => e.filterId !== varietyFilterId)
                    : [
                        // intentionally only adding the filter for variance by ignoring other filters.
                        // ...prevState.filters.filter(e => e.filterId !== varietyFilterId),
                        {
                            filterId: varietyFilterId,
                            value: info.variety,
                        }
                    ],
                keyword: undefined,
                variance: prevState.variance?.variety === info.variety ? undefined : {partNo: info.partNo ?? info.partNumber, variety: info.variety},
            })
        });
    }, [setSearchData, varietyFilterId])

    const onAggregatedSelectionChange = useCallback((nextCard) => {
        applySelection(nextCard, setProducts);
    }, [applySelection]);

    /**
     * if there are no products, not loading, there is a search token and the search limit is not exceeded, it means that there are results in other industries.
     */
    useEffect(() => {
        if (!(!loadingProducts && !products?.length && !inProductsList && !!searchToken && !readonly)) {
            setOtherIndustriesHaveResults(false);
        }
    }, [loadingProducts, products, inProductsList, searchToken, readonly])

    return (
        <>
            <Box ref={containerRef} sx={{display: 'flex', flexDirection: 'column', gap: 1, padding: 2}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1}}>
                    <Typography variant={'body2'} fontWeight={600}>
                        {
                            aggregationActive
                                ? ((!showMainResultsLoading || !!products.length) && tProducts('results'))
                                : ((!showMainResultsLoading || !!paginationInfo.length) &&
                                    tProducts('showingCount', {count: paginationInfo.length}))
                        }
                    </Typography>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1}}>
                        {
                            otherIndustriesHaveResults &&
                            <Button variant={'text'} color={'primary'} onClick={() => document.getElementById('products-from-other-industries')?.scrollIntoView({block: 'start'})}>
                                Other Industries have results
                                <ArrowDownwardOutlined sx={{marginInlineStart: 0.5,}}/>
                            </Button>
                        }
                        {
                            inProductsList && aggregationSupported &&
                            <Stack sx={TOGGLE_TOOLBAR_STACK_SX}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={aggregationToggleOn ? 'aggregation' : ''}
                                >
                                    <ToggleButton
                                        value="aggregation"
                                        selected={aggregationToggleOn}
                                        disabled={loadingAggregationToggle}
                                        onClick={toggleAggregation}
                                        sx={{
                                            backgroundColor: aggregationToggleOn ? 'divider' : 'transparent',
                                        }}
                                    >
                                        <Tooltip title={tProducts('aggregationToggleTooltip')}>
                                            <ViewModuleOutlined sx={{padding: 0.25}}/>
                                        </Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Stack>
                        }
                        <Stack sx={TOGGLE_TOOLBAR_STACK_SX}>
                            <ToggleButtonGroup
                                disabled={loadingViewMode}
                                sx={TOGGLE_TOOLBAR_GROUP_SX}
                                size={'small'}
                                value={viewMode}
                                onChange={(_, v) => toggleViewMode(v)}
                                exclusive
                            >
                                <ToggleButton
                                    value={"list"}
                                    sx={{backgroundColor: viewMode === 'list' ? "divider" : "transparent"}}>
                                    <Tooltip title={'List View'}>
                                        <SortRounded sx={{padding: 0.25}}/>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton
                                    value={"grid"}
                                    sx={{backgroundColor: viewMode === 'grid' ? "divider" : "transparent"}}>
                                    <Tooltip title={"Grid View"}>
                                        <ViewCompactRounded sx={{padding: 0.25}}/>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Box>
                </Box>
                {
                    !loadingViewMode && viewMode === 'grid' &&
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMoreData}
                        hasMore={hasMoreToLoad}
                        useWindow={false}
                        getScrollParent={getScrollParent}
                        threshold={400}
                    >

                        <Grid container spacing={3} margin={0}>
                            {
                                mainProducts?.map((product) => (
                                    <Grid key={product?.info?.varietyKey ?? product?.id} xs={12} sm={6} md={12} mdlg={6} lgxl={4} xxl={3}>
                                        {
                                            (aggregationActive || product.info?.isAggregated)
                                                ? <AggregatedLookupProductCard
                                                    readonly={readonly}
                                                    info={product.info}
                                                    priceInfo={product.priceInfo}
                                                    availabilityInfo={product.availabilityInfo}
                                                    additionalProperties={product.additionalProperties}
                                                    link={product.link}
                                                    canGetExtraInfo={!!selectedPartner || !!selectedWarehouseIds?.length}
                                                    comparing={comparing}
                                                    inCompareList={inCompareList}
                                                    gettingExtraInfo={gettingExtraInfo}
                                                    toggleInCompareList={toggleInCompareList}
                                                    onGetExtraInfoClicked={onGetExtraInfoClicked}
                                                    onProductClicked={onProductClicked}
                                                    selectedVariety={selectedVariety}
                                                    onVarianceClicked={onVarianceClicked}
                                                    varianceProperties={varianceProperties}
                                                    onAggregatedSelectionChange={onAggregatedSelectionChange}
                                                />
                                                : <LookupProductCard
                                                    readonly={readonly}
                                                    info={product.info}
                                                    priceInfo={product.priceInfo}
                                                    availabilityInfo={product.availabilityInfo}
                                                    additionalProperties={product.additionalProperties}
                                                    link={product.link}
                                                    canGetExtraInfo={!!selectedPartner || !!selectedWarehouseIds?.length}
                                                    comparing={comparing}
                                                    inCompareList={inCompareList}
                                                    gettingExtraInfo={gettingExtraInfo}
                                                    toggleInCompareList={toggleInCompareList}
                                                    onGetExtraInfoClicked={onGetExtraInfoClicked}
                                                    onProductClicked={onProductClicked}
                                                    selectedVariety={selectedVariety}
                                                    onVarianceClicked={onVarianceClicked}
                                                />
                                        }
                                    </Grid>
                                ))
                            }
                            {
                                !!relevantProducts.length &&
                                <>
                                    {
                                        relevantProducts.length !== products.length &&
                                        <Grid xs={12}>
                                            <Typography
                                                variant={'subtitle1'}
                                                marginBlockStart={4}
                                                marginBlockEnd={2}
                                                fontWeight={600}
                                            >
                                                Other Relevant Products
                                            </Typography>
                                        </Grid>
                                    }
                                    {
                                        relevantProducts?.map((product) => (
                                            <Grid key={product?.info?.varietyKey ?? product?.id} xs={12} sm={6} md={12} mdlg={6} lgxl={4} xxl={3}>
                                                <LookupProductCard
                                                    readonly={readonly}
                                                    info={product.info}
                                                    priceInfo={product.priceInfo}
                                                    availabilityInfo={product.availabilityInfo}
                                                    additionalProperties={product.additionalProperties}
                                                    link={product.link}
                                                    canGetExtraInfo={!!selectedPartner || !!selectedWarehouseIds?.length}
                                                    comparing={comparing}
                                                    inCompareList={inCompareList}
                                                    gettingExtraInfo={gettingExtraInfo}
                                                    toggleInCompareList={toggleInCompareList}
                                                    onGetExtraInfoClicked={onGetExtraInfoClicked}
                                                    onProductClicked={onProductClicked}
                                                />
                                            </Grid>
                                        ))
                                    }
                                </>
                            }
                        </Grid>
                    </InfiniteScroll>
                }
                {
                    showMainResultsLoading && viewMode === 'grid' &&
                    <Grid container spacing={3} margin={0}>
                        {
                            Array.from({length: 8}).map((e, index) =>
                                <Grid key={index} xs={12} sm={6} md={12} mdlg={6} lgxl={4} xxl={3}>
                                    <Skeleton height={350} sx={{transform: 'none'}}/>
                                </Grid>
                            )
                        }
                    </Grid>
                }
                {
                    !loadingViewMode && viewMode === 'list' &&
                    <ProductsDataGrid
                        paginationInfo={paginationInfo}
                        readonly={readonly}
                        mainProducts={mainProducts}
                        relevantProducts={relevantProducts}
                        canGetExtraInfo={!!selectedPartner || !!selectedWarehouseIds?.length}
                        inProductsList={inProductsList}
                        loading={showMainResultsLoading}
                        hasMoreToLoad={hasMoreToLoad}
                        loadMoreData={loadPageData}
                        comparing={comparing}
                        inCompareList={inCompareList}
                        gettingExtraInfo={gettingExtraInfo}
                        toggleInCompareList={toggleInCompareList}
                        onGetExtraInfoClicked={onGetExtraInfoClicked}
                        onProductClicked={onProductClicked}
                        onVarianceClicked={onVarianceClicked}
                        selectedVariety={selectedVariety}
                        varianceProperties={varianceProperties}
                        onAggregatedSelectionChange={onAggregatedSelectionChange}
                    />
                }
                {
                    showMainResultsLoading && viewMode === 'list' &&
                    <Grid container spacing={0} margin={0}>
                        {
                            Array.from({length: 1}).map((e, index) =>
                                <Grid key={index} xs={12}>
                                    <Skeleton height={700} sx={{transform: 'none'}}/>
                                </Grid>
                            )
                        }
                    </Grid>
                }
                {
                    operatorAccessLevels.packages.lookup.submitLostSearch &&
                    !showMainResultsLoading && !products?.length &&
                    <ProductsLostSearchContainer
                        readonly={readonly}
                        searchToken={searchToken}
                        searchSchema={searchSchema}
                        searchData={searchData}
                        setSearchData={setSearchData}
                        showForm={!!searchToken && !inProductsList}
                    />
                }
                {
                    !showMainResultsLoading && !products?.length && !inProductsList && !!searchToken && !readonly &&
                    <ProductsOtherIndustriesContainer
                        industries={industries}
                        searchData={searchData}
                        setSearchData={setSearchData}
                        searchSchema={searchSchema}
                        isAdvancedMode={advancedSearch}
                        performNexusSearch={performNexusSearch}
                        getProductNavigationLink={getProductNavigationLink}
                        submitProductSelection={submitProductSelection}
                        setOtherIndustriesHaveResults={setOtherIndustriesHaveResults}
                        headerId={'products-from-other-industries'}
                    />
                }
            </Box>
        </>
    );
}


export default LookupSearchViewProducts;

